// library functions
import { Back } from "gsap/EasePack";
import { TimelineLite } from "gsap/TimelineLite";
import { TweenLite } from "gsap/TweenLite";

// singletons
import { GameState } from "Models/GameState";

// static data
import * as Constants from "Data/Constants";
import * as VO from "Data/VO";

// pubsub topics
import * as Topics from "Data/Topics";
import {
    REGISTER_FOCUSABLES,
    UNREGISTER_FOCUSABLE,
    UNREGISTER_FOCUSABLES,
 } from "html-living-framework/lib/Data/Topics";

// gameobjects
import { Button } from "GameObjects/Button";
import { HUD } from "GameObjects/HUD";

// types and interfaces
import { Application } from "Application";
import { GameConfig, ItemID } from "Data/GameConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";

export class InteriorHUD extends HUD {

// #region children
    public readonly startButton: Button;
    public readonly continueButton: Button;
    public readonly cheatButton: Button;
    public readonly homeButtonGiveQuest: Button;
    public readonly homeButtonReturnItems: Button;
// #endregion

// #region local state
    protected _subscriptions: any[] = [];
    protected animateInTimeline?: gsap.Animation;
    protected state?: ILevelState;
// #endregion

    constructor() {
        super();
        // this.margin.bottom = 11.5;

        this.cheatButton = new Button(
            this.HandleCheatButtonClicked.bind(this),
            "button-paint-fill",
            Constants.SPRITESHEET_UI,
        );
        this.cheatButton.position.set(this.cheatButton.width / 2, this.cheatButton.height / 2);
        this.cheatButton.addText("(CHEAT)\nreturn all", Constants.FONT_GEL_MED_22, 16);
        // this.TopLeft.addChild(this.cheatButton); // Uncomment this to show the cheat button
        this.cheatButton.scale.set(0.5);
        this.cheatButton.position.set(this.cheatButton.width / 2, this.cheatButton.height / 2);

        this.startButton = new Button(
            this.HandleStartButtonClicked.bind(this),
            "next-btn-1",
            Constants.SPRITESHEET_UI,
            VO.UI.GIVE.UI10,
            "ui_click",
        );
        this.BottomCenter.addChild(this.startButton);
        this.startButton.position.set(0, -this.startButton.height / 2);

        this.homeButtonReturnItems = new Button(
            this.HandleHomeButtonClicked.bind(this),
            "home-button",
            Constants.SPRITESHEET_UI,
            VO.UI.UI9,
            "ui_click",
        );
        this.BottomCenter.addChild(this.homeButtonReturnItems);
        this.homeButtonReturnItems.position.set(
            -this.homeButtonReturnItems.width / 2 - 10,
            -this.homeButtonReturnItems.height / 2,
        );

        this.homeButtonGiveQuest = new Button(
            this.HandleHomeButtonClicked.bind(this),
            "exit-button",
            Constants.SPRITESHEET_UI,
            VO.UI.UI9,
            "ui_click",
        );
        this.TopLeft.addChild(this.homeButtonGiveQuest);
        this.homeButtonGiveQuest.position.set(
            this.homeButtonGiveQuest.width / 2 + 8,
            this.homeButtonGiveQuest.height * 1.5 + 8,
        );

        this.continueButton = new Button(
            this.HandleContinueButtonClicked.bind(this),
            "next-btn-1",
            Constants.SPRITESHEET_UI,
            VO.UI.RETURN.UI8,
            "ui_click",
        );
        this.BottomCenter.addChild(this.continueButton);
        this.continueButton.position.set(this.continueButton.width / 2 + 10, -this.continueButton.height / 2);

        this._subscriptions.push(
            PubSub.subscribe(Topics.ITEM_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ALL_ITEMS_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.LEVEL_COMPLETE, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.SHOW_CONTINUE_BUTTON, this.onMessageReceived.bind(this)),
        );

    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        super.init(pSize, pData); // calls onResize

        this.state = pData;

        this.cheatButton.visible = false;
        this.cheatButton.interactive = false;
        this.startButton.visible = false;
        this.startButton.interactive = false;
        this.continueButton.visible = false;
        this.continueButton.interactive = false;
        this.homeButtonReturnItems.visible = false;
        this.homeButtonReturnItems.interactive = false;

        this.homeButtonGiveQuest.visible = true;
        this.homeButtonGiveQuest.interactive = true;

        if (pData.allItemsFound) { // return items
            this.cheatButton.visible = true;
            this.cheatButton.interactive = true;

            this.homeButtonGiveQuest.visible = false;
            this.homeButtonGiveQuest.interactive = false;
        }

        return this;
    }

    public animateIn() {
        super.animateIn();
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.animateInTimeline?.kill();
        super.destroy(pOptions);
    }

    protected HandleStartButtonClicked() {
        PubSub.publishSync(UNREGISTER_FOCUSABLES, [this.homeButtonGiveQuest, this.startButton]);
        GameState.Instance.GoToExterior();
    }

    protected HandleHomeButtonClicked() {
        PubSub.publishSync(UNREGISTER_FOCUSABLES, [this.homeButtonReturnItems, this.continueButton]);
        Application.instance.voiceover.stopVO();
        GameState.Instance.GoToLevelSelect();
    }

    protected HandleCheatButtonClicked() {
        for (const item of GameState.Instance.currentLevelConfig.hiddenitems) {
            const _item = item.item;
            if (typeof _item === "string") {
                GameState.Instance.ReturnItem(_item as ItemID);
            }
            else {
                GameState.Instance.ReturnItem(_item.id);
            }
        }
    }

    protected HandleContinueButtonClicked() {
        PubSub.publishSync(UNREGISTER_FOCUSABLES, [this.homeButtonReturnItems, this.continueButton]);
        GameState.Instance.GoToExterior();
        // new TimelineLite().call(() => {
        //     PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
        //         Constants.STATE_LEVEL_SELECT,
        //         Constants.LOAD_INTERSTITIAL,
        //         ...Constants.DEFAULT_TRANSITION,
        //     ));
        // }).delay(1);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.LEVEL_COMPLETE:
                this.OnLevelCompleted(pData as ILevelState);
                break;
            case Topics.ITEM_RETURNED:
                this.OnItemReturned(pData as IItemState);
                break;
            case Topics.ALL_ITEMS_RETURNED:
                this.OnAllItemsReturned(pData as ILevelState);
                break;
            case Topics.SHOW_CONTINUE_BUTTON:
                this.ShowContinueButton(pData as ILevelState);
                break;
        }
    }

    protected OnItemReturned(state: IItemState) {
        this.state!.items[state.id] = state;
    }

    protected OnAllItemsReturned(state: ILevelState) {
        this.state = state;
        this.cheatButton.visible = false;
        this.cheatButton.interactive = false;
        PubSub.publishSync(UNREGISTER_FOCUSABLE, this.cheatButton);
    }

    protected ShowContinueButton(state: ILevelState) {
        if (state.allItemsFound && state.allItemsReturned) {
            const locationConfig = GameConfig.locations[state.id];
            const narrativeConfig = GameConfig.locations[state.id].narratives[state.narrative];
            const narrativeVariationConfig =
                GameConfig.locations[state.id].narratives[state.narrative].variations[state.narrativeVariation];
            const vo = {...narrativeVariationConfig.vo, ...narrativeConfig.vo, ...locationConfig.vo};

            const buttons = [this.continueButton, this.homeButtonReturnItems];
            buttons.forEach((b) => {
                b.visible = true;
                b.interactive = true;
            });
            this.animateInTimeline = TweenLite
                .fromTo(buttons, 0.5,
                    {pixi: {scale: 0}},
                    {pixi: {scale: 1}, ease: Back.easeOut},
                )
                .delay(2.25)
                .eventCallback(Constants.TWEEN_ONCOMPLETE, () => {
                    PubSub.publishSync(REGISTER_FOCUSABLES, buttons);
                })
            ;
        }
        else {
            const button = this.startButton;
            if (button.visible) {
                this.animateInTimeline = new TimelineLite()
                    .to(button, 0.5,
                        {pixi: {scale: 1.125}},
                    )
                    .to(button, 0.5,
                        {pixi: {scale: 1}},
                    )
                    .to(button, 0.5,
                        {pixi: {scale: 1.125}},
                    )
                    .to(button, 0.5,
                        {pixi: {scale: 1}},
                    )
                    .to(button, 0.5,
                        {pixi: {scale: 1.125}},
                    )
                    .to(button, 0.5,
                        {pixi: {scale: 1}},
                    )
                ;
            }
            else {
                button.visible = true;
                button.interactive = true;
                this.animateInTimeline = new TimelineLite()
                    .fromTo([this.startButton, this.homeButtonReturnItems], 0.5,
                        {pixi: {scale: 0}},
                            {pixi: {scale: 1}},
                        )
                        .call(() => {
                            PubSub.publishSync(REGISTER_FOCUSABLES, [this.startButton, this.homeButtonReturnItems]);
                        })
                        .to(button, 0.5,
                            {pixi: {scale: 1.125}},
                        )
                        .to(button, 0.5,
                            {pixi: {scale: 1}},
                        )
                        .to(button, 0.5,
                            {pixi: {scale: 1.125}},
                        )
                        .to(button, 0.5,
                            {pixi: {scale: 1}},
                        )
                        .to(button, 0.5,
                            {pixi: {scale: 1.125}},
                        )
                        .to(button, 0.5,
                            {pixi: {scale: 1}},
                    )
                    .delay(1.25)
                ;
            }
        }
    }

    protected OnLevelCompleted(state: ILevelState) {
        PubSub.publishSync(UNREGISTER_FOCUSABLES, [
            this.continueButton,
            this.startButton,
            this.cheatButton,
            this.homeButtonGiveQuest,
            this.homeButtonReturnItems,
        ]);
    }

}
